/**
 * @fileoverview Authoritative, immutable US state and territory constants.
 */

/**
 * Canonical map of USPS abbreviations to their full state / territory names
 * (50 states + Washington D.C.). This is the single source of truth — both
 * {@link US_STATE_ABBREVIATIONS} and any other derived shape come from this
 * map's keys / values, so the abbreviation list cannot drift from the name
 * list.
 *
 * Use cases:
 * - Look up the full name for a 2-letter code (e.g. for backend payloads
 *   that store a human-readable label alongside the canonical code).
 * - Render a dropdown showing full state names while preserving the
 *   2-letter code as the form value.
 *
 * The object literal is alphabetized by abbreviation; JS preserves insertion
 * order on plain objects so `Object.keys(...)` returns the abbreviations in
 * the same order.
 *
 * @type {Readonly<Record<string, string>>}
 */
export const US_STATE_NAMES_BY_ABBREVIATION = Object.freeze({
  AK: 'Alaska',
  AL: 'Alabama',
  AR: 'Arkansas',
  AZ: 'Arizona',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DC: 'District of Columbia',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  IA: 'Iowa',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  MA: 'Massachusetts',
  MD: 'Maryland',
  ME: 'Maine',
  MI: 'Michigan',
  MN: 'Minnesota',
  MO: 'Missouri',
  MS: 'Mississippi',
  MT: 'Montana',
  NC: 'North Carolina',
  ND: 'North Dakota',
  NE: 'Nebraska',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NV: 'Nevada',
  NY: 'New York',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VA: 'Virginia',
  VT: 'Vermont',
  WA: 'Washington',
  WI: 'Wisconsin',
  WV: 'West Virginia',
  WY: 'Wyoming',
});

/**
 * The complete list of valid US state and territory postal abbreviations
 * accepted throughout the application (50 states + Washington D.C.).
 *
 * Derived from {@link US_STATE_NAMES_BY_ABBREVIATION} so the two cannot
 * drift. Values are two-letter USPS abbreviations in ascending alphabetical
 * order. The array is frozen to prevent mutation of the shared module-level
 * reference.
 *
 * @type {Readonly<string[]>}
 */
export const US_STATE_ABBREVIATIONS = Object.freeze(
  Object.keys(US_STATE_NAMES_BY_ABBREVIATION),
);

/**
 * @typedef {Object} StateAbbreviationOption
 * @property {string} label - Display label shown in dropdown UI (e.g. `"CA"`)
 * @property {string} value - Form value submitted on selection (e.g. `"CA"`)
 */

/**
 * Dropdown option objects for all entries in {@link US_STATE_ABBREVIATIONS},
 * shaped as `{ label, value }` for use with Dropdown components.
 *
 * Both the outer array and each option object are frozen.
 *
 * @type {Readonly<StateAbbreviationOption[]>}
 */
export const US_STATE_ABBREVIATION_DROPDOWN_OPTIONS = Object.freeze(
  US_STATE_ABBREVIATIONS.map((state) =>
    Object.freeze({ label: state, value: state }),
  ),
);

/**
 * Canonical map of USPS abbreviations to their two-digit US Census FIPS state
 * codes (50 states + Washington D.C.). Use this when calling APIs that
 * identify states by FIPS-2 (e.g. the trip-service administrative-areas
 * endpoints) while the UI keeps the two-letter abbreviation as its form
 * value. Order matches {@link US_STATE_NAMES_BY_ABBREVIATION}.
 *
 * @type {Readonly<Record<string, string>>}
 */
export const US_STATE_FIPS_BY_ABBREVIATION = Object.freeze({
  AK: '02',
  AL: '01',
  AR: '05',
  AZ: '04',
  CA: '06',
  CO: '08',
  CT: '09',
  DC: '11',
  DE: '10',
  FL: '12',
  GA: '13',
  HI: '15',
  IA: '19',
  ID: '16',
  IL: '17',
  IN: '18',
  KS: '20',
  KY: '21',
  LA: '22',
  MA: '25',
  MD: '24',
  ME: '23',
  MI: '26',
  MN: '27',
  MO: '29',
  MS: '28',
  MT: '30',
  NC: '37',
  ND: '38',
  NE: '31',
  NH: '33',
  NJ: '34',
  NM: '35',
  NV: '32',
  NY: '36',
  OH: '39',
  OK: '40',
  OR: '41',
  PA: '42',
  RI: '44',
  SC: '45',
  SD: '46',
  TN: '47',
  TX: '48',
  UT: '49',
  VA: '51',
  VT: '50',
  WA: '53',
  WI: '55',
  WV: '54',
  WY: '56',
});

/**
 * Inverse of {@link US_STATE_FIPS_BY_ABBREVIATION}. Use when a backend
 * response carries FIPS-2 codes and the UI needs to render the familiar
 * two-letter abbreviation.
 *
 * @type {Readonly<Record<string, string>>}
 */
export const US_STATE_ABBREVIATION_BY_FIPS = Object.freeze(
  Object.fromEntries(
    Object.entries(US_STATE_FIPS_BY_ABBREVIATION).map(([abbr, fips]) => [
      fips,
      abbr,
    ]),
  ),
);
